import { formatTime as formatTimeOfDay, type TimeFormatPreference } from '@doccareplus/shared/lib/date';
import type { AppointmentResponse } from '../infrastructure/scheduling.api';

/**
 * Pure view helpers for the scheduling surfaces — formatting and derivations
 * only, no I/O. Kept framework-free so they're trivially unit-testable.
 *
 * Time-of-day rendering goes through the shared `formatTime` seam and defaults
 * to 12-hour. Callers that live in components read the user preference via
 * `useTimeFormat()` and pass it in; the default keeps these helpers usable from
 * non-component code and tests without wiring a store.
 */

/** Local wall-clock time for a slot; 12-hour by default, e.g. "11:00 AM". */
export function formatTime(iso: string, timeFormat: TimeFormatPreference = '12h'): string {
  return formatTimeOfDay(iso, timeFormat);
}

/** Local date + wall-clock time for a slot, e.g. "20 Jul, 11:00 AM" — used where the day matters (requests). */
export function formatDateTime(iso: string, timeFormat: TimeFormatPreference = '12h'): string {
  const date = new Date(iso);
  const day = date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  return `${day}, ${formatTime(iso, timeFormat)}`;
}

/** Local time range for a slot, e.g. "11:00 AM – 11:30 AM". */
export function formatTimeRange(
  startIso: string,
  durationMinutes: number,
  timeFormat: TimeFormatPreference = '12h'
): string {
  const end = new Date(new Date(startIso).getTime() + durationMinutes * 60_000);
  return `${formatTime(startIso, timeFormat)} – ${formatTime(end.toISOString(), timeFormat)}`;
}

/**
 * Minutes a checked-in patient has been waiting, from check-in until `now`
 * (or until they were started, if already in progress). Null when not yet
 * checked in. Never negative.
 */
export function waitMinutes(appointment: AppointmentResponse, now: Date = new Date()): number | null {
  if (appointment.waitMinutes != null) {
    return appointment.waitMinutes;
  }
  if (!appointment.checkedInAt) {
    return null;
  }
  const end = appointment.startedAt ? new Date(appointment.startedAt) : now;
  const diffMs = end.getTime() - new Date(appointment.checkedInAt).getTime();
  return Math.max(0, Math.round(diffMs / 60_000));
}

/**
 * Whole minutes from `now` until an ISO instant (negative once past). Kept in this non-component module so the
 * clock read stays out of React render bodies (the purity rule forbids `Date.now()` during render).
 */
export function minutesUntil(iso: string, now: Date = new Date()): number {
  return Math.floor((new Date(iso).getTime() - now.getTime()) / 60_000);
}

/** Minutes a visit has been in progress (from `startedAt` until `now`); null when not yet started. Never negative. */
export function minutesInVisit(appointment: AppointmentResponse, now: Date = new Date()): number | null {
  if (!appointment.startedAt) return null;
  return Math.max(0, Math.round((now.getTime() - new Date(appointment.startedAt).getTime()) / 60_000));
}

/**
 * i18n key suffix for a status, mapping the C# PascalCase status name to the
 * snake_case keys used in appointments.json (`status.*`).
 */
export function statusKey(status: string): string {
  return status.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();
}

/** True for statuses that belong on the active queue board. */
export function isActiveQueueStatus(status: string): boolean {
  return status !== 'Cancelled' && status !== 'Rejected';
}

/** Local yyyy-MM-dd for `now` — the `min` for date inputs that must not accept past days. */
export function todayDateInput(now: Date = new Date()): string {
  const m = (now.getMonth() + 1).toString().padStart(2, '0');
  const d = now.getDate().toString().padStart(2, '0');
  return `${now.getFullYear()}-${m}-${d}`;
}

/**
 * Local wall-clock "HH:mm" for `now`, rounded up to the next `stepMinutes`
 * increment — the sensible default for a new booking's time field. Rounding up
 * (rather than to the exact minute) keeps the pre-filled slot from landing a few
 * seconds in the past by the time staff submit, so it clears the not-in-past guard.
 */
export function currentTimeInput(now: Date = new Date(), stepMinutes = 5): string {
  const rounded = new Date(now);
  rounded.setSeconds(0, 0);
  const remainder = rounded.getMinutes() % stepMinutes;
  if (remainder !== 0) {
    rounded.setMinutes(rounded.getMinutes() + (stepMinutes - remainder));
  }
  const h = rounded.getHours().toString().padStart(2, '0');
  const m = rounded.getMinutes().toString().padStart(2, '0');
  return `${h}:${m}`;
}

/**
 * Earliest selectable time-of-day for a date field, as a `TimeField` `minTime` — the exact
 * current wall-clock time when `date` is today (so already-past slots show disabled rather
 * than only failing on submit), or `undefined` for any other day (no floor).
 */
export function minSelectableTimeInput(date: string, now: Date = new Date()): string | undefined {
  if (!date || date !== todayDateInput(now)) return undefined;
  const h = now.getHours().toString().padStart(2, '0');
  const m = now.getMinutes().toString().padStart(2, '0');
  return `${h}:${m}`;
}

/**
 * Latest selectable date for staff scheduled booking, as a `DateField` `max` — 30 days out
 * from `now`. Client-side v1 cap so staff can't drift years into the future by mistake; see
 * `.ai/BusinessDomain/scheduling-queue.md` § Future-Dated Booking Window.
 */
export function maxBookableDateInput(now: Date = new Date()): string {
  const max = new Date(now);
  max.setDate(max.getDate() + 30);
  return todayDateInput(max);
}

/**
 * True when the chosen local date + time is before `now` — the client-side
 * pre-check that mirrors the server's `appointment.startTimeInPast` guard.
 * Empty/invalid entries are treated as not-past (nothing to validate yet);
 * exactly-now is allowed.
 */
export function isBookingInPast(date: string, time: string, now: Date = new Date()): boolean {
  if (!date || !time) return false;
  const start = new Date(`${date}T${time}`);
  if (Number.isNaN(start.getTime())) return false;
  return start.getTime() < now.getTime();
}

/** Convert an on-site clinic wall-clock selection into the absolute instant sent to the API. */
export function bookingStartTimeIso(date: string, time: string): string {
  return new Date(`${date}T${time}`).toISOString();
}

/**
 * Return the canonical server slot matching a custom wall-clock choice. Strict scheduling uses this
 * before enabling submit; the command still revalidates the slot to protect against stale availability.
 */
export function findServerApprovedStartTime(
  date: string,
  time: string,
  availableStartTimes: readonly string[],
): string | null {
  if (!date || !time) return null;
  const candidate = new Date(`${date}T${time}`).getTime();
  if (Number.isNaN(candidate)) return null;
  return availableStartTimes.find((startTime) => new Date(startTime).getTime() === candidate) ?? null;
}

/** Half-open UTC window covering the local day that contains `date`. */
export function dayWindowUtc(date: Date): { from: string; to: string } {
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const end = new Date(start.getTime());
  end.setDate(end.getDate() + 1);
  return { from: start.toISOString(), to: end.toISOString() };
}

/** Local midnight of the Monday that starts the week containing `date`. */
export function startOfWeek(date: Date): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = d.getDay(); // 0=Sun … 6=Sat
  const backToMonday = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + backToMonday);
  return d;
}

/** The six working days Mon–Sat of the week containing `date` (local midnights). */
export function weekDays(date: Date): Date[] {
  const monday = startOfWeek(date);
  return Array.from({ length: 6 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
}

/** Half-open UTC window covering the local Mon–Sun week containing `date`. */
export function weekWindowUtc(date: Date): { from: string; to: string } {
  const monday = startOfWeek(date);
  const end = new Date(monday);
  end.setDate(monday.getDate() + 7);
  return { from: monday.toISOString(), to: end.toISOString() };
}
